import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

import { AxpoButtonComponent } from '../../core/axpo-button/axpo-button.component';
import { AxpoDialogComponent } from '../../core/axpo-dialog/axpo-dialog.component';
import {
  AxpoFormElementComponent,
  IFormError,
} from '../../core/axpo-form-element/axpo-form-element.component';
import { AxpoSearchComponent } from '../../core/axpo-search/axpo-search.component';
import { AxpoSpinnerComponent } from '../../core/axpo-spinner/axpo-spinner.component';
import { AxpoTableNavigationComponent } from '../../core/axpo-tablenavigation/axpo-tablenavigation.component';
import { AxpoTypographyComponent } from '../../core/axpo-typography/axpo-typography.component';
import { SvgService } from '../../core/services/svg.service';
import { ITabItem, TabsComponent } from '../../shared/controls/tabs/tabs.component';
import {
  IExternalGroup,
  IExternalGroupMember,
  IExternalUser,
} from '../../shared/models/api_models';
import { ExternalGroupService } from '../../shared/services/external-group.service';
import { ExternalUserService } from '../../shared/services/external-user.service';
import { FormDebounceService } from '../../shared/services/form-debounce.service';
import { FormValidator } from '../../shared/utils/formValidator';

const ContactManagementTabIds = {
  USERS: 'users',
  GROUPS: 'groups',
} as const;

@Component({
  selector: 'app-contact-management',
  standalone: true,
  imports: [
    TranslocoPipe,
    AxpoTypographyComponent,
    AxpoButtonComponent,
    AxpoSearchComponent,
    AxpoSpinnerComponent,
    AxpoFormElementComponent,
    AxpoDialogComponent,
    TabsComponent,
    AxpoTableNavigationComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './contact-management.component.html',
  styleUrl: './contact-management.component.css',
})
export class ContactManagementComponent implements OnInit {
  svgService = inject(SvgService);
  externalUserService = inject(ExternalUserService);
  externalGroupService = inject(ExternalGroupService);
  translocoService = inject(TranslocoService);
  debounceService = inject(FormDebounceService);

  page = signal<number>(1);
  pageSize = signal<number>(10);
  searchTerm = signal<string | undefined>(undefined);
  appliedSearchTerm = signal<string | undefined>(undefined);
  appliedSearchTerms = computed(() =>
    (this.appliedSearchTerm() ?? '')
      .trim()
      .split(/\s+/)
      .filter(term => term.length > 0),
  );

  users = computed(() => this.externalUserService.externalUsers());
  totalUsers = computed(() => this.externalUserService.totalExternalUsers());
  isLoading = computed(() => this.externalUserService.loadingExternalUsers());
  isSavingUser = computed(() => this.externalUserService.savingExternalUser());
  isDeletingUser = computed(() => this.externalUserService.deletingExternalUser());
  isDeletingGroup = computed(() => this.externalGroupService.deletingExternalGroup());
  isInitialLoad = computed(() => this.isLoading() && this.users() === undefined);

  selectedTabId = signal<string>(ContactManagementTabIds.USERS);
  groupPage = signal<number>(1);
  groupPageSize = signal<number>(10);
  groupSearchTerm = signal<string | undefined>(undefined);
  appliedGroupSearchTerm = signal<string | undefined>(undefined);

  groups = computed(() => this.externalGroupService.externalGroups());
  totalGroups = computed(() => this.externalGroupService.totalExternalGroups());
  isGroupsLoading = computed(() => this.externalGroupService.loadingExternalGroups());
  isGroupsInitialLoad = computed(() => this.isGroupsLoading() && this.groups() === undefined);

  showGroupDialog = signal<boolean>(false);
  editingGroup = signal<IExternalGroup | undefined>(undefined);
  formGroupName = signal<string | undefined>(undefined);
  isSavingGroup = computed(() => this.externalGroupService.savingExternalGroup());
  groupMembers = computed(() => this.externalGroupService.groupDetail()?.members ?? []);
  isLoadingGroupDetail = computed(() => this.externalGroupService.loadingGroupDetail());
  isRemovingGroupMember = computed(() => this.externalGroupService.removingGroupMember());
  memberSearchTerm = signal<string | undefined>(undefined);
  searchResults = computed(() => this.externalGroupService.searchResults());
  isSearchingMembers = computed(() => this.externalGroupService.loadingSearchResults());
  isAddingGroupMember = computed(() => this.externalGroupService.addingGroupMember());

  groupDialogTitle = computed(() =>
    this.editingGroup()
      ? 'contactManagement.editGroupDialogTitle'
      : 'contactManagement.addGroupDialogTitle',
  );
  groupSaveDisabled = computed(() => this.isSavingGroup() || !this.formGroupName());
  keepGroupDialogOpen = computed(() => !this.editingGroup()?.id);

  isGroupsTab = computed(() => this.selectedTabId() === 'groups');
  tabs = signal<ITabItem[]>([
    { id: ContactManagementTabIds.USERS, title: 'contactManagement.tabs.users' },
    { id: ContactManagementTabIds.GROUPS, title: 'contactManagement.tabs.groups' },
  ]);

  getSvg = this.svgService.svgMap;

  showUserDialog = signal<boolean>(false);
  editingUser = signal<IExternalUser | undefined>(undefined);
  showDeleteDialog = signal<boolean>(false);
  userToDelete = signal<IExternalUser | undefined>(undefined);
  showDeleteGroupDialog = signal<boolean>(false);
  groupToDelete = signal<IExternalGroup | undefined>(undefined);
  formName = signal<string | undefined>(undefined);
  formEmail = signal<string | undefined>(undefined);
  formPhone = signal<string | undefined>(undefined);
  formDesignation = signal<string | undefined>(undefined);
  formManager: FormValidator | undefined;

  dialogTitle = computed(() =>
    this.editingUser()
      ? 'contactManagement.editUserDialogTitle'
      : 'contactManagement.addUserDialogTitle',
  );
  saveDisabled = computed(
    () =>
      this.isSavingUser() ||
      !this.formName() ||
      !this.formEmail() ||
      !this.formPhone() ||
      !this.formDesignation(),
  );

  constructor() {
    effect(() => {
      this.externalUserService.getExternalUsers(
        this.page(),
        this.pageSize(),
        this.appliedSearchTerms(),
      );
    });

    effect(() => {
      if (this.selectedTabId() !== 'groups') {
        return;
      }
      this.externalGroupService.getExternalGroups(
        this.groupPage(),
        this.groupPageSize(),
        this.appliedGroupSearchTerm() ?? '',
      );
    });
  }

  ngOnInit(): void {
    this.svgService.loadSvg(['edit', 'delete', 'user-add', 'add-group']);
    this.initFormManager();
  }

  private initFormManager() {
    this.formManager = new FormValidator(
      [
        {
          key: 'name',
          errorMessageSignal: signal(undefined),
          errorIdSignal: signal(undefined),
        },
        {
          key: 'email',
          errorMessageSignal: signal(undefined),
          errorIdSignal: signal(undefined),
        },
        {
          key: 'phone',
          errorMessageSignal: signal(undefined),
          errorIdSignal: signal(undefined),
        },
        {
          key: 'designation',
          errorMessageSignal: signal(undefined),
          errorIdSignal: signal(undefined),
        },
      ],
      this.translocoService,
      this.debounceService,
    );
  }

  openAdd() {
    if (this.selectedTabId() === 'groups') {
      this.openAddGroup();
    } else {
      this.openAddUser();
    }
  }

  openAddUser() {
    this.externalUserService.savingExternalUser.set(false);
    this.editingUser.set(undefined);
    this.formName.set(undefined);
    this.formEmail.set(undefined);
    this.formPhone.set(undefined);
    this.formDesignation.set(undefined);
    this.showUserDialog.set(true);
  }

  openEditUser(user: IExternalUser) {
    this.externalUserService.savingExternalUser.set(false);
    this.editingUser.set(user);
    this.formName.set(user.name ?? undefined);
    this.formEmail.set(user.email ?? undefined);
    this.formPhone.set(user.phone ?? undefined);
    this.formDesignation.set(user.designation ?? undefined);
    this.showUserDialog.set(true);
  }

  closeUserDialog(action: 'confirm' | 'cancel') {
    if (action === 'cancel') {
      this.showUserDialog.set(false);
      return;
    }

    if (this.isSavingUser()) {
      return;
    }

    this.formManager?.checkRequired('name', this.formName());
    this.formManager?.checkRequired('email', this.formEmail());
    this.formManager?.checkRequired('phone', this.formPhone());
    this.formManager?.checkRequired('designation', this.formDesignation());
    if (!this.formManager?.isValid) {
      this.formManager?.showErrors();
      return;
    }

    const payload: IExternalUser = {
      name: this.formName() ?? '',
      email: this.formEmail() ?? '',
      phone: this.formPhone() ?? '',
      designation: this.formDesignation(),
    };

    if (this.editingUser()?.id) {
      this.externalUserService.updateExternalUser({ ...payload, id: this.editingUser()?.id });
    } else {
      this.externalUserService.createExternalUser(payload);
    }

    this.searchTerm.set('');
    this.appliedSearchTerm.set(undefined);
    this.page.set(1);
    this.showUserDialog.set(false);
  }

  formValidation(formError: IFormError | undefined, key: string) {
    const field = this.formManager?.getField(key);
    const fieldErrorId = field?.errorId();
    if (formError) {
      this.formManager?.setError(
        formError.formId,
        formError.value,
        formError?.message,
        formError.replacement,
      );
    } else if (fieldErrorId && fieldErrorId > -1) {
      this.formManager?.clearError(key);
    }
  }

  tabSelectionChanged(tab: ITabItem) {
    this.selectedTabId.set(tab.id);
    if (tab.id === ContactManagementTabIds.USERS) {
      this.externalUserService.clearCache();
      this.searchTerm.set('');
      this.appliedSearchTerm.set(undefined);
      this.page.set(1);
    } else if (tab.id === 'groups') {
      this.externalGroupService.clearCache();
      this.groupSearchTerm.set('');
      this.appliedGroupSearchTerm.set(undefined);
      this.groupPage.set(1);
    }
  }

  search() {
    this.appliedSearchTerm.set(this.searchTerm());
    this.page.set(1);
  }

  groupSearch() {
    this.appliedGroupSearchTerm.set(this.groupSearchTerm());
    this.groupPage.set(1);
  }

  editUser(user: IExternalUser) {
    this.openEditUser(user);
  }

  deleteUser(user: IExternalUser) {
    this.userToDelete.set(user);
    this.showDeleteDialog.set(true);
  }

  deleteUserDialogClosed(action: 'confirm' | 'cancel') {
    if (action === 'confirm' && this.userToDelete()?.id) {
      this.page.set(1);
      this.externalUserService.deleteExternalUser(this.userToDelete()!.id!);
    }
    this.userToDelete.set(undefined);
    this.showDeleteDialog.set(false);
  }

  editGroup(group: IExternalGroup) {
    this.openEditGroup(group);
  }

  deleteGroup(group: IExternalGroup) {
    this.groupToDelete.set(group);
    this.showDeleteGroupDialog.set(true);
  }

  deleteGroupDialogClosed(action: 'confirm' | 'cancel') {
    if (action === 'confirm' && this.groupToDelete()?.id) {
      this.groupPage.set(1);
      this.externalGroupService.deleteExternalGroup(this.groupToDelete()!.id!);
    }
    this.groupToDelete.set(undefined);
    this.showDeleteGroupDialog.set(false);
  }

  openAddGroup() {
    this.externalGroupService.savingExternalGroup.set(false);
    this.editingGroup.set(undefined);
    this.formGroupName.set(undefined);
    this.memberSearchTerm.set(undefined);
    this.externalGroupService.searchResults.set([]);
    this.externalGroupService.groupDetail.set(undefined);
    this.showGroupDialog.set(true);
  }

  openEditGroup(group: IExternalGroup) {
    this.externalGroupService.savingExternalGroup.set(false);
    this.editingGroup.set(group);
    this.formGroupName.set(group.name ?? undefined);
    this.memberSearchTerm.set(undefined);
    this.externalGroupService.searchResults.set([]);
    this.externalGroupService.getGroupDetail(group.id ?? 0);
    this.showGroupDialog.set(true);
  }

  closeGroupDialog(action: 'confirm' | 'cancel') {
    if (action === 'cancel') {
      this.showGroupDialog.set(false);
      return;
    }

    if (this.isSavingGroup()) {
      return;
    }

    const name = this.formGroupName()?.trim() ?? '';
    if (!name) {
      return;
    }

    if (this.editingGroup()?.id) {
      this.externalGroupService.updateExternalGroup(this.editingGroup()!.id!, name);
    } else {
      this.externalGroupService.createExternalGroup(name, createdGroup => {
        this.editingGroup.set(createdGroup);
        this.formGroupName.set(createdGroup.name ?? name);
        this.memberSearchTerm.set(undefined);
        this.externalGroupService.searchResults.set([]);
        if (createdGroup.id) {
          this.externalGroupService.getGroupDetail(createdGroup.id);
        }
      });
    }
    if (this.editingGroup()?.id) {
      this.showGroupDialog.set(false);
    }

    this.groupSearchTerm.set('');
    this.appliedGroupSearchTerm.set(undefined);
    this.groupPage.set(1);
    this.showGroupDialog.set(false);
  }

  removeGroupMember(member: IExternalGroupMember) {
    const groupId = this.editingGroup()?.id;
    if (!groupId || !member.type || !member.id) {
      return;
    }
    this.externalGroupService.removeGroupMember(groupId, member.type, member.id);
  }

  searchMembers() {
    this.externalGroupService.searchMembers(this.editingGroup()?.id, this.memberSearchTerm() ?? '');
  }

  addMember(member: IExternalGroupMember) {
    const groupId = this.editingGroup()?.id;
    if (!groupId) {
      return;
    }
    this.externalGroupService.addGroupMember(groupId, member, () => {
      this.memberSearchTerm.set(undefined);
    });
  }
}
